import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar, ChevronRight, ChevronLeft, Download, Loader2, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import BulkAddReportsModal from '../components/BulkAddReportsModal';

const AdminReports = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [showDeleteRange, setShowDeleteRange] = useState(false);
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const [busy, setBusy] = useState(false);
  const limit = 30;

  useEffect(() => {
    if (!user?.is_admin) { navigate('/login'); return; }
    loadReports();
  }, [page, dateFilter]);

  const loadReports = async () => {
    setLoading(true);
    try {
      let url = `/admin/reports?limit=${limit}&skip=${page * limit}`;
      if (dateFilter) url += `&date_filter=${dateFilter}`;
      const res = await api.get(url);
      setReports(res.data.reports);
      setTotal(res.data.total);
      setSelected(new Set());
    } catch (err) {
      toast.error('فشل تحميل التقارير');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      let url = '/admin/reports/export';
      if (dateFilter) url += `?date_filter=${dateFilter}`;
      const res = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `التقارير_${dateFilter || 'الكل'}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success('تم تصدير التقارير بنجاح');
    } catch { toast.error('فشل تصدير التقارير'); }
    finally { setExporting(false); }
  };

  const toggleAll = () => {
    if (selected.size === reports.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(reports.map(r => r.id)));
    }
  };

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const bulkDeleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`سيتم حذف ${selected.size} تقرير. هل أنت متأكد؟`)) return;
    setBusy(true);
    try {
      const res = await api.post('/admin/reports/bulk-delete', { ids: Array.from(selected) });
      toast.success(`تم حذف ${res.data.deleted} تقرير — أُعيد حساب ${res.data.affected_readers} قارئ`);
      await loadReports();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'فشل الحذف');
    } finally { setBusy(false); }
  };

  const bulkDeleteAllFiltered = async () => {
    const scope = dateFilter ? `التقارير بتاريخ ${dateFilter}` : `جميع التقارير (${total})`;
    if (!window.confirm(`سيتم حذف ${scope}. هذا الإجراء نهائي. هل أنت متأكد؟`)) return;
    if (!window.confirm('تأكيد نهائي: هل تريد حقاً الحذف؟')) return;
    setBusy(true);
    try {
      const payload = { delete_all: true };
      if (dateFilter) payload.date_filter = dateFilter;
      const res = await api.post('/admin/reports/bulk-delete', payload);
      toast.success(`تم حذف ${res.data.deleted} تقرير — أُعيد حساب ${res.data.affected_readers} قارئ`);
      setPage(0);
      await loadReports();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'فشل الحذف');
    } finally { setBusy(false); }
  };

  const bulkDeleteRange = async () => {
    if (!rangeStart && !rangeEnd) { toast.error('حدد تاريخاً واحداً على الأقل'); return; }
    let label;
    if (rangeStart && rangeEnd) label = `من ${rangeStart} إلى ${rangeEnd}`;
    else if (rangeStart) label = `من ${rangeStart} فما بعد`;
    else label = `حتى ${rangeEnd}`;
    if (!window.confirm(`سيتم حذف كل التقارير ${label}. هذا الإجراء نهائي. هل أنت متأكد؟`)) return;
    if (!window.confirm('تأكيد نهائي: هل تريد حقاً الحذف؟')) return;
    setBusy(true);
    try {
      const payload = { delete_all: true };
      if (rangeStart) payload.start_date = rangeStart;
      if (rangeEnd) payload.end_date = rangeEnd;
      const res = await api.post('/admin/reports/bulk-delete', payload);
      toast.success(`تم حذف ${res.data.deleted} تقرير — أُعيد حساب ${res.data.affected_readers} قارئ`);
      setShowDeleteRange(false);
      setRangeStart('');
      setRangeEnd('');
      setPage(0);
      await loadReports();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'فشل الحذف');
    } finally { setBusy(false); }
  };

  const totalPages = Math.ceil(total / limit);
  const allSelectedOnPage = reports.length > 0 && selected.size === reports.length;

  return (
    <div className="space-y-6" data-testid="admin-reports">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="text-3xl font-bold text-accent">التقارير القرائية</h1>
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => setShowBulkAdd(true)} className="bg-primary text-white hover:bg-primary/90" data-testid="bulk-add-btn">
            <Plus className="w-4 h-4 ml-2" />
            إضافة تقارير
          </Button>
          <Button onClick={handleExport} disabled={exporting} variant="outline" className="border-primary text-primary hover:bg-primary/10" data-testid="export-btn">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : <Download className="w-4 h-4 ml-2" />}
            تصدير Excel
          </Button>
          <Calendar className="w-5 h-5 text-muted-foreground" />
          <Input
            type="date"
            value={dateFilter}
            onChange={(e) => { setDateFilter(e.target.value); setPage(0); }}
            className="w-48"
            data-testid="date-filter"
          />
          {dateFilter && (
            <Button variant="outline" size="sm" onClick={() => { setDateFilter(''); setPage(0); }}>
              الكل
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-muted-foreground">اجمالي: {total} تقرير · مُحدَّد: {selected.size}</p>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <Button
              onClick={bulkDeleteSelected}
              disabled={busy}
              variant="outline"
              className="border-red-500 text-red-600 hover:bg-red-50"
              data-testid="bulk-delete-selected-btn"
            >
              <Trash2 className="w-4 h-4 ml-2" />
              حذف المحدَّد ({selected.size})
            </Button>
          )}
          <Button
            onClick={bulkDeleteAllFiltered}
            disabled={busy || total === 0}
            variant="outline"
            className="border-red-500 text-red-600 hover:bg-red-50"
            data-testid="bulk-delete-all-btn"
          >
            <Trash2 className="w-4 h-4 ml-2" />
            {dateFilter ? `حذف تقارير هذا اليوم (${total})` : `حذف الكل (${total})`}
          </Button>
          <Button
            onClick={() => setShowDeleteRange((v) => !v)}
            disabled={busy}
            variant="outline"
            className="border-red-500 text-red-600 hover:bg-red-50"
            data-testid="toggle-delete-range-btn"
          >
            <Trash2 className="w-4 h-4 ml-2" />
            حذف نطاق تواريخ
          </Button>
        </div>
      </div>

      {showDeleteRange && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex flex-wrap items-end gap-3" data-testid="delete-range-panel">
          <div className="space-y-1">
            <label className="text-xs text-red-700 font-semibold">من تاريخ</label>
            <Input
              type="date"
              value={rangeStart}
              onChange={(e) => setRangeStart(e.target.value)}
              className="w-48"
              data-testid="range-start-input"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-red-700 font-semibold">إلى تاريخ</label>
            <Input
              type="date"
              value={rangeEnd}
              onChange={(e) => setRangeEnd(e.target.value)}
              className="w-48"
              data-testid="range-end-input"
            />
          </div>
          <Button
            onClick={bulkDeleteRange}
            disabled={busy || (!rangeStart && !rangeEnd)}
            className="bg-red-600 text-white hover:bg-red-700"
            data-testid="confirm-delete-range-btn"
          >
            <Trash2 className="w-4 h-4 ml-2" />
            حذف تقارير النطاق
          </Button>
          <Button variant="ghost" onClick={() => { setShowDeleteRange(false); setRangeStart(''); setRangeEnd(''); }}>
            إلغاء
          </Button>
          <p className="text-xs text-red-700 basis-full">
            نصيحة: للحذف بيوم واحد فقط، اجعل «من» و«إلى» بنفس التاريخ. اترك أحدهما فارغاً للحذف قبل/بعد تاريخ معين.
          </p>
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-primary/5 border-b-2 border-primary/20">
                <th className="text-right py-4 px-3 w-10">
                  <Checkbox
                    checked={allSelectedOnPage}
                    onCheckedChange={toggleAll}
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th className="text-right py-4 px-4 font-bold text-accent">التاريخ</th>
                <th className="text-right py-4 px-4 font-bold text-accent">الوقت</th>
                <th className="text-right py-4 px-4 font-bold text-accent">الرقم</th>
                <th className="text-right py-4 px-4 font-bold text-accent">الاسم</th>
                <th className="text-right py-4 px-4 font-bold text-accent">التصنيف</th>
                <th className="text-right py-4 px-4 font-bold text-accent">العنوان</th>
                <th className="text-right py-4 px-4 font-bold text-accent">الصفحات</th>
                <th className="text-right py-4 px-4 font-bold text-accent">مكتمل</th>
                <th className="text-right py-4 px-4 font-bold text-accent">الرصيد</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="text-center py-8 text-muted-foreground">جارٍ التحميل...</td></tr>
              ) : reports.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-8 text-muted-foreground">لا توجد تقارير</td></tr>
              ) : (
                reports.map((rp) => (
                  <tr
                    key={rp.id}
                    className={`border-b border-muted/20 hover:bg-muted/10 transition-colors ${selected.has(rp.id) ? 'bg-primary/5' : ''}`}
                  >
                    <td className="py-3 px-3">
                      <Checkbox
                        checked={selected.has(rp.id)}
                        onCheckedChange={() => toggleOne(rp.id)}
                        data-testid={`select-report-${rp.id}`}
                      />
                    </td>
                    <td className="py-3 px-4">{rp.date}</td>
                    <td className="py-3 px-4">{rp.time}</td>
                    <td className="py-3 px-4 font-mono">{rp.reader_number}</td>
                    <td className="py-3 px-4 font-semibold">{rp.reader_name}</td>
                    <td className="py-3 px-4">{rp.classification}</td>
                    <td className="py-3 px-4 max-w-[200px] truncate">{rp.title}</td>
                    <td className="py-3 px-4 font-bold text-primary">{rp.pages_read}</td>
                    <td className="py-3 px-4">{rp.is_completed}</td>
                    <td className="py-3 px-4">{rp.balance}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-muted/20">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
              <ChevronRight className="w-4 h-4 ml-1" /> السابق
            </Button>
            <span className="text-sm text-muted-foreground">صفحة {page + 1} من {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
              التالي <ChevronLeft className="w-4 h-4 mr-1" />
            </Button>
          </div>
        )}
      </Card>

      {showBulkAdd && (
        <BulkAddReportsModal
          onClose={() => setShowBulkAdd(false)}
          onSuccess={() => { setShowBulkAdd(false); loadReports(); }}
        />
      )}
    </div>
  );
};

export default AdminReports;
